import { Device } from "api/types/device";
import { t } from "i18next";
import { CONST } from "models/constants";
import { DeviceConfiguration } from "models/devices/configuration";
import LabeledField from "providers/labeledField";
import { useState } from "react";
import { FaEdit } from "react-icons/fa";
import { TbCircuitSwitchOpen } from "react-icons/tb";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { getReadings } from "util/device";
import DeviceCard from "widgets/cards/device";
import DeviceInfoDialog from "widgets/dialogs/deviceInfo";
import SwitchValveDialog from "widgets/dialogs/switchValve";
import CircleButton from "widgets/controls/buttons/circle-button";
import QrCodeButton from "widgets/controls/buttons/qrcode";

interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
  onUpdate?: () => void;
}

const General: React.FC<GeneralProps> = ({ device, config, onUpdate }) => {
  const sites = useSelector(accountSiteList, shallowEqual);
  const [isValve, setIsValve] = useState(false);

  const [showDeviceInfo, setShowDeviceInfo] = useState(false);

  const handleClose = (refresh: boolean) => {
    setShowDeviceInfo(false);
    if (refresh) {
      onUpdate();
    }
  };

  const getSiteName = (id: string) => {
    const site = sites.find((a) => a.id === id);
    return site ? site.name : null;
  };

  return (
    <>
      {isValve && (
        <SwitchValveDialog
          isOpen={isValve}
          device={device}
          onClose={() => {
            setIsValve(false);
          }}
          onSuccess={(refresh) => {
            setIsValve(false);
            if (refresh) onUpdate();
          }}
        />
      )}
      {showDeviceInfo && (
        <DeviceInfoDialog
          device={device}
          isOpen={showDeviceInfo}
          onClose={() => handleClose(false)}
          onSuccess={() => handleClose(true)}
        />
      )}
      <DeviceCard
        showIcon
        title={device?.name}
        subTitle={`${t("generic.serial")}: ${device.snr || ""}`}
        subTitle1={`${t("meter.reading")}: ${getReadings(device)}`}
        config={config}
        topRight={
          <div className="-mt-2 flex">
            {device?.valveState && (
              <CircleButton
                icon={TbCircuitSwitchOpen}
                size="md"
                onClick={() => {
                  setIsValve(!isValve);
                }}
                permissionRole={CONST.ROLE_OWNER}
                tooltip={t("tooltip.valve")}
                tooltipPlacement="bottom"
                color={
                  device?.valve1Synced
                    ? device?.valveState === "OPEN"
                      ? "green"
                      : "red"
                    : "amber"
                }
              />
            )}
            <CircleButton
              icon={FaEdit}
              size="md"
              variant="ghost"
              onClick={() => {
                setShowDeviceInfo(true);
              }}
              permissionRole={CONST.ROLE_MANAGER}
              tooltip={t("tooltip.edit.device")}
              tooltipPlacement="bottom"
              color="white"
            />
            <QrCodeButton data={device?.snr} type="snr" />
          </div>
        }
      >
        <div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("label.meter.number")}
              id={"meter-number"}
            >
              <div className="text-txt-lg text-white">
                {device?.meterNumber || "Number"}
              </div>
            </LabeledField>
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("label.meter.model")}
              id={"meter-model"}
            >
              <div className="text-txt-lg text-white">
                {device?.meterModel || t("label.meter.model")}
              </div>
            </LabeledField>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("label.host")}
              id={"meter-host"}
            >
              <div className="text-txt-lg text-white">
                {device?.host || t("label.host")}
              </div>
            </LabeledField>
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("label.unit.number")}
              id={"meter-unit-number"}
            >
              <div className="text-txt-lg text-white">
                {device?.unitNumber || t("label.unit.number")}
              </div>
            </LabeledField>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
            <LabeledField
              className="text-txt-xs"
              textColor="white"
              label={t("label.classes")}
              id={"meter-classes"}
            >
              <div className="text-txt-lg text-white">
                {getSiteName(device?.classes[0]) || t("label.classes")}
              </div>
            </LabeledField>
          </div>
        </div>
      </DeviceCard>
    </>
  );
};

export default General;
